import {
    useCallback,
    useEffect,
    useMemo,
    useState,
} from 'react';
import { useStore } from 'zustand';
import { useAuth } from '@/entities/Auth/store';
import { useChatDialogWorkspace } from '@/entities/ChatDialog/model/workspace';
import { appDialogToCompanion } from '@/entities/AppChat/mappers';
import { createAppChatDetailsStore } from '@/entities/AppChat/model/detailsStore';
import { useCanAccessPermission } from '@/shared/model/adminAccess';
import { ActionButton } from '@/shared/ui/ActionButton/ActionButton';
import { SidePanel } from '@/shared/ui/SidePanel/SidePanel';
import { RelatedOperatorTasks } from '@/features/operatorTasks/ui/RelatedOperatorTasks';
import { appDialogToTaskOrigin } from '@/features/operatorTasks/model/origins';
import { CompanionDetails } from './CompanionDetails';

/** Открывает сведения собеседника и задачи поддержки */
export const AppChatActions = ({ readOnly }: { readOnly: boolean }) => {
    const dialog = useChatDialogWorkspace((state) => state.selectedDialog);
    const companion = useMemo(() => appDialogToCompanion(dialog), [dialog]);
    const origin = useMemo(() => appDialogToTaskOrigin(dialog), [dialog]);
    const canViewClients = useCanAccessPermission('admin.clients.view');
    const canViewExecutors = useCanAccessPermission('admin.executors.view');

    const canViewProfile = companion?.role === 'Client'
        ? canViewClients
        : canViewExecutors;

    const [open, setOpen] = useState(false);
    const store = useMemo(() => createAppChatDetailsStore(), []);
    const details = useStore(store);
    const sessionRevision = useAuth((state) => state.sessionRevision);

    /** Загружает сведения выбранного собеседника */
    const load = useCallback(() => {
        if (companion && canViewProfile) void store.getState().load(companion.id, companion.role);
    }, [companion, canViewProfile, store]);

    useEffect(() => {
        setOpen(false);
        store.getState().reset();

        return () => store.getState().reset();
    }, [dialog?.id, sessionRevision, store]);

    useEffect(() => {
        if (open && canViewProfile) load();
        if (!canViewProfile) store.getState().reset();
    }, [open, canViewProfile, load, store]);

    return <>
        {!readOnly && origin &&
            <RelatedOperatorTasks
                origin={origin}
                variant="chat"
            />
        }
        {companion && canViewProfile &&
            <ActionButton
                type="button"
                label="О пользователе"
                onClick={() => setOpen(true)}
            />
        }
        <SidePanel
            visible={open && canViewProfile && Boolean(companion)}
            title="Собеседник"
            onHide={() => setOpen(false)}
        >
            {companion &&
                <CompanionDetails
                    companion={companion}
                    userInfo={details.data}
                    status={details.status}
                    error={details.error}
                    onRetry={load}
                />
            }
        </SidePanel>
    </>;
};
